// @story HIR-HR3
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { resumeApi } from '../../services/resumeApi';
import { resumeUploadService } from '../../services/resumeUpload.service';
import ResumesContent from '../../components/common/table/ResumesTable';
import UploadResumeContent from '../../components/common/resume/UploadResumeContent';
import ProcessingStatusBadge from '../../components/common/resume/ProcessingStatusBadge';
import { ExistingCvsButton } from '../../components/talent/ExistingCvMatches';
import BulkPreviewModal from '../../components/common/modal/BulkPreviewModal';
import ResumeUploadModal from '../../components/common/resume/ResumeUpload';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Button, Card, CardHeader, Checkbox, EmptyState, Icon, Input, Select } from '../../ds';
import { Loading } from '../../components/page';
import DocumentChaseList from './DocumentChaseList';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'shortlisted', label: 'Shortlisted' },
  // Under the CV bar: ordered lower, never rejected (HIR-HR10).
  { value: 'review_later', label: 'Review later' },
  { value: 'rejected', label: 'Not Shortlisted' },
];

// @story HIR-HR3
// refreshKey: bumped by the vacancy page when people are added from its own
// "Also check our existing CVs" button, so this list shows them at once.
const ResumeManager = ({ refreshKey = 0 }) => {
  const { id: jobId } = useParams();
  const [resumes, setResumes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalResumes, setTotalResumes] = useState(0);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1,
    limit: 10
  });
  const [currentFilter, setCurrentFilter] = useState('all');
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    isUploading: false,
    message: ''
  });

  // Bulk invite state
  const [selectedIds,    setSelectedIds]    = useState([]);
  const [bulkStageId,    setBulkStageId]    = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [previewing,     setPreviewing]     = useState(false);
  const [previewData,    setPreviewData]    = useState(null); // { drafts, stage, job }

  // The vacancy's title, for the "Also check our existing CVs" window (F11).
  const [jobTitle, setJobTitle] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Screening thresholds
  const [thresholds, setThresholds] = useState({ jdMatchThreshold: 60, atsScoreThreshold: 60 });
  const [thresholdOpen, setThresholdOpen] = useState(false);
  const [savingThresholds, setSavingThresholds] = useState(false);

  const [jobPipelineStages, setJobPipelineStages] = useState([]);

  useEffect(() => {
    if (!jobId) return;
    api.get(`/jobs/${jobId}`).then(r => {
      const data = r.data?.data || r.data || {};
      const cfg  = data.screeningConfig || {};
      setJobTitle(data.title || '');
      setThresholds({
        jdMatchThreshold:  cfg.jdMatchThreshold  ?? 60,
        atsScoreThreshold: cfg.atsScoreThreshold ?? 60,
      });
      // Populate stage picker from job pipeline so ALL stages are available
      const pipeline = (cfg.pipeline || []).filter(s => s.enabled !== false);
      setJobPipelineStages(pipeline.map(s => ({ stageId: s.stageId, label: s.label || s.type })));
    }).catch(() => {});
  }, [jobId]);

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const resp = await api.patch(`/jobs/${jobId}/screening-thresholds`, thresholds);
      // The bar orders the list; it never changes anyone's status (HIR-HR10).
      const above = resp.data?.aboveBar ?? 0;
      const below = resp.data?.belowBar ?? 0;
      toast.success(`Thresholds saved. ${above} above the bar, ${below} below it to review later. Nobody's status was changed.`);
      setThresholdOpen(false);
      fetchResumes();
    } catch (e) {
      const status = e?.response?.status ?? e?.status;
      toast.error(status === 403
        ? 'Only a recruitment admin can change the screening thresholds.'
        : 'Failed to save thresholds');
    } finally {
      setSavingThresholds(false);
    }
  };

  // Reprocess functions
  const handleReprocessResume = async (resumeId) => {
    try {
      console.log('Reprocessing resume with job context:', { resumeId, jobId });
      const result = await resumeApi.reprocessResume(resumeId, jobId);
      console.log('Reprocess result:', result);
      // Refresh the resumes list
      fetchResumes();
      return result;
    } catch (error) {
      console.error('Error reprocessing resume:', error);
      // Show user-friendly error message
      toast.error(error.message || 'Failed to reprocess resume');
      throw error;
    }
  };

  // quiet: refresh in place, without swapping the tab for the spinner. A
  // background refresh (the 15s poll, people just added from the existing
  // CVs) must not close a window that is open on top of the list.
  const fetchResumes = async (page = 1, filter = currentFilter, { quiet = false } = {}) => {
    try {
      console.log('Fetching resumes for jobId:', jobId);
      if (!quiet) setIsLoading(true);
      const response = await resumeApi.getAllResumes(jobId, page, pagination.limit, filter);
      console.log('Resumes response:', response);
      console.log('Resumes found:', response.data.resumes?.length || 0);
      
      setResumes(response.data.resumes || []);
      setTotalResumes(response.data.totalResumes);
      setPagination({
        total: response.data.total,
        page: response.data.page || 1,
        totalPages: response.data.totalPages || 1,
        limit: pagination.limit,
        statusCounts: response.data.statusCounts
      });
      setCurrentFilter(filter);
    } catch (error) {
      console.error('Error fetching resumes:', error);
    } finally {
      if (!quiet) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchResumes(1, 'all');
    }
  }, [jobId]);

  // People added from the vacancy page's header button: show them now.
  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) { firstRefresh.current = false; return; }
    if (jobId) fetchResumes(pagination.page, currentFilter, { quiet: true });
    // Only a new refreshKey should refetch; the page and filter are read as they are then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Auto-refresh every 5s while any resume is still being processed
  useEffect(() => {
    const hasProcessing = resumes.some(r =>
      r.processingStatus === 'uploaded' ||
      r.processingStatus === 'queued' ||
      r.processingStatus === 'processing'
    );
    if (!hasProcessing) return;

    const interval = setInterval(() => {
      fetchResumes(pagination.page, currentFilter, { quiet: true });
    }, 15000);

    return () => clearInterval(interval);
  }, [resumes, pagination.page, currentFilter]);

  const handleUpload = async (files) => {
    try {

      setUploadProgress({
        current: 0,
        total: files.length,
        isUploading: true,
        message: 'Starting upload...'
      });
      
      setIsModalOpen(false);
      
      // Use the new batch upload service
      const uploadResult = await resumeUploadService.uploadResumesBatch(jobId, files, (progress) => {
        setUploadProgress(prev => ({
          ...prev,
          current: Math.round((progress.progress / 100) * files.length),
          message: progress.message || 'Uploading files...'
        }));
      });
      
      // Refresh the resumes list after batch upload
      await fetchResumes(1, currentFilter);
      
      // Show success message
      if (uploadResult.success) {
        const successCount = uploadResult.uploadedFiles?.length || 0;
        const failedCount = uploadResult.failedFiles?.length || 0;
        
        if (failedCount > 0) {
          console.warn(`${successCount} files uploaded successfully, ${failedCount} failed`);
        } else {
          console.log(`${successCount} files uploaded successfully`);
        }
      }
      
    } catch (error) {
      console.error('Error uploading resumes:', error);
      toast.error(`Upload failed: ${error.message}`);
      // Still refresh — server may have partially succeeded
      try { await fetchResumes(1, currentFilter); } catch (_) {}
    } finally {
      setUploadProgress({
        current: 0,
        total: 0,
        isUploading: false,
        message: ''
      });
    }
  };

  const handleStatusChange = async (resumeId, type, status, override = false) => {
    try {
      await resumeApi.updateResumeStatus(resumeId, type, status, override);
      fetchResumes(pagination.page, currentFilter);
    } catch (error) {
      console.error('Error updating resume status:', error);
      toast.error('Failed to update resume status');
    }
  };

  const handleDelete = async (resumeId) => {
    try {
      await resumeApi.deleteResume(resumeId);
      fetchResumes(pagination.page, currentFilter);
    } catch (error) {
      console.error('Error deleting resume:', error);
      toast.error('Failed to delete resume');
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchResumes(pagination.page, currentFilter);
    } catch (error) {
      console.error('Error refreshing resumes:', error);
      toast.error('Failed to refresh resumes');
    } finally {
      setRefreshing(false);
    }
  };

  // Derive pipeline stages from resumes' pipelineStageResults for the stage picker
  const pipelineStages = React.useMemo(() => {
    const seen = new Map();
    resumes.forEach(r => {
      (r.pipelineStageResults || []).forEach(s => {
        if (!seen.has(s.stageId)) seen.set(s.stageId, s.label || s.stageId);
      });
    });
    return [...seen.entries()].map(([stageId, label]) => ({ stageId, label }));
  }, [resumes]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === resumes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(resumes.map(r => r._id));
    }
  };

  // Step 1 — generate drafts and open preview modal
  const handlePreviewInvites = async () => {
    if (!bulkStageId) { toast.error('Select a pipeline stage first'); return; }
    if (selectedIds.length === 0) { toast.error('Select at least one candidate'); return; }

    setPreviewing(true);
    try {
      const res = await api.post('/invites/bulk/preview', {
        resumeIds: selectedIds,
        stageId:   bulkStageId,
        jobId,
      });
      setPreviewData(res.data.data); // { drafts, stage, job }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate email previews');
    } finally {
      setPreviewing(false);
    }
  };

  // Step 2 — HR approved, fire the actual sends with (possibly edited) drafts
  const handleApproveAndSend = async (approvedDrafts) => {
    setSendingInvites(true);
    try {
      const res = await api.post('/invites/bulk', {
        resumeIds: approvedDrafts.map(d => d.resumeId),
        stageId:   bulkStageId,
        jobId,
        drafts:    approvedDrafts, // pre-generated subjects + bodies
      });
      const { sent, failed, total } = res.data.data;
      toast.success(`Sent ${sent}/${total} invite emails${failed > 0 ? ` (${failed} failed)` : ''}`);
      setPreviewData(null);
      setSelectedIds([]);
      setBulkStageId('');
      fetchResumes(pagination.page, currentFilter);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk invite failed');
    } finally {
      setSendingInvites(false);
    }
  };

  if (isLoading) {
    return <Card><Loading label="Loading resumes" /></Card>;
  }

  const handlePageChange = (newPage, filter = currentFilter) => {
    setCurrentFilter(filter);
    fetchResumes(newPage, filter);
  };

  if (!isLoading && resumes.length === 0 && currentFilter === 'all') {
    return (
      <UploadResumeContent
        jobId={jobId}
        onUpload={handleUpload}
      />
    );
  }

  // The filters, as buttons: the chosen one is the primary.
  const filterButtons = FILTERS.map((f) => (
    <Button
      key={f.value}
      size="sm"
      variant={currentFilter === f.value ? 'primary' : 'secondary'}
      onClick={() => handlePageChange(1, f.value)}
    >
      {f.label}
    </Button>
  ));

  // Filter returned no results — show an empty state instead of upload screen
  if (!isLoading && resumes.length === 0 && currentFilter !== 'all') {
    return (
      <Card>
        <CardHeader title="Resumes" action={<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{filterButtons}</div>} />
        <EmptyState
          icon={<Icon name="file-text" size={36} />}
          title="No resumes found"
          description="No candidates match the selected filter."
          action={<div><Button variant="secondary" size="sm" onClick={() => handlePageChange(1, 'all')}>View all resumes</Button></div>}
        />
      </Card>
    );
  }

  const allSelected = selectedIds.length === resumes.length && resumes.length > 0;
  const stageOptions = (jobPipelineStages.length ? jobPipelineStages : pipelineStages).map((s) => ({ value: s.stageId, label: s.label }));

  return (
    <div style={{ display: 'grid', gap: 24, minWidth: 0 }}>
      {uploadProgress.isUploading && (
        <Card padding="16px 20px">
          <div role="status" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10, fontSize: 'var(--fs-body-md)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-1)', fontWeight: 500 }}>
              <Icon name="spinner" spin />
              {uploadProgress.message || 'Uploading resumes'}
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--status-danger-text)', fontWeight: 400 }}>(Do Not Close This Page)</span>
            </span>
            <span className="num" style={{ color: 'var(--text-2)' }}>
              {uploadProgress.current} of {uploadProgress.total}
            </span>
          </div>
          <div style={{ width: '100%', height: 8, borderRadius: 'var(--radius-pill)', background: 'var(--surface-2)', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 'var(--radius-pill)', background: 'var(--brand-primary)', transition: 'width var(--duration-fast)', width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }} />
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Resumes"
          subtitle={totalResumes ? `${totalResumes} on this vacancy` : undefined}
          action={<ProcessingStatusBadge jobId={jobId} />}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {filterButtons}
          <span style={{ flex: 1 }} />
          <Button
            size="sm"
            icon={<Icon name="file-arrow-up" />}
            onClick={() => setIsModalOpen(true)}
            disabled={uploadProgress.isUploading}
          >
            Upload More
          </Button>

          {/* Where "Fetch Resumes" was: the school's existing CVs, scored by
              the Talent-pool matcher against this vacancy (F11). The list
              refreshes as soon as people are added. */}
          <ExistingCvsButton
            jobId={jobId}
            jobTitle={jobTitle}
            onAdded={() => fetchResumes(pagination.page, currentFilter, { quiet: true })}
          />
        </div>
      </Card>

      {/* Screening Threshold Settings */}
      <Card padding="0">
        <button
          onClick={() => setThresholdOpen(o => !o)}
          aria-expanded={thresholdOpen}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontWeight: 600, color: 'var(--text-1)', fontSize: 'var(--fs-body-md)' }}>
            <Icon name="gear" />
            Screening Thresholds
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>
              (JD Match ≥ {thresholds.jdMatchThreshold}% · ATS ≥ {thresholds.atsScoreThreshold}%)
            </span>
          </span>
          <Icon name="caret-down" style={{ color: 'var(--text-muted)', transform: thresholdOpen ? 'rotate(180deg)' : 'none' }} />
        </button>
        {thresholdOpen && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
            <p style={{ margin: '0 0 12px', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
              A CV is above the bar only if BOTH thresholds are met. CVs below the bar are not rejected: they stay in the list, ordered lower, to review later. Saving changes the order only, never anyone's status.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-end' }}>
              {[
                { key: 'jdMatchThreshold',  label: 'JD Match %' },
                { key: 'atsScoreThreshold', label: 'ATS Score %' },
              ].map(({ key, label }) => (
                <div key={key} style={{ width: 140 }}>
                  <Input
                    type="number"
                    id={`threshold-${key}`}
                    label={label}
                    hint="out of 100"
                    value={thresholds[key]}
                    onChange={e => setThresholds(t => ({ ...t, [key]: Number(e.target.value) }))}
                  />
                </div>
              ))}
              <Button onClick={saveThresholds} disabled={savingThresholds}>
                {savingThresholds ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <div style={{ minWidth: 0 }}>
        {/* Select-all row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
          <Checkbox
            checked={allSelected}
            indeterminate={!allSelected && selectedIds.length > 0}
            onChange={toggleSelectAll}
            label={selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}
          />
          {selectedIds.length > 0 && (
            <Button size="sm" variant="ghost" aria-label="Clear selection" icon={<Icon name="x" />} onClick={() => setSelectedIds([])} />
          )}
        </div>

        {/* HIR-HR26: who to ask for which documents. Never a rejection. */}
        <div style={{ marginBottom: 12 }}><DocumentChaseList jobId={jobId} /></div>

        <ResumesContent
          resumes={resumes}
          pagination={pagination}
          setPagination={setPagination}
          onPageChange={handlePageChange}
          currentFilter={currentFilter}
          screenType="resumeScreening"
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onReprocess={handleReprocessResume}
          onRefresh={() => fetchResumes(pagination.page, currentFilter)}
          loading={isLoading}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
        />
      </div>

      {/* Floating bulk invite bar */}
      {selectedIds.length > 0 && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 50, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: '10px 16px', maxWidth: 'calc(100vw - 32px)' }}>
          <Icon name="paper-plane" style={{ color: 'var(--brand-primary)' }} />
          <span style={{ fontSize: 'var(--fs-body-md)', fontWeight: 600, color: 'var(--text-1)' }}>{selectedIds.length} candidate{selectedIds.length !== 1 ? 's' : ''} selected</span>
          <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>→</span>
          <div style={{ minWidth: 200 }}>
            <Select
              value={bulkStageId}
              onChange={setBulkStageId}
              placeholder="Pick a stage…"
              options={stageOptions}
            />
          </div>
          <Button
            size="sm"
            onClick={handlePreviewInvites}
            disabled={previewing || !bulkStageId}
            icon={<Icon name={previewing ? 'spinner' : 'eye'} spin={previewing} />}
          >
            {previewing ? 'Drafting…' : 'Preview & Send'}
          </Button>
          <Button size="sm" variant="ghost" aria-label="Clear selection" icon={<Icon name="x" />} onClick={() => setSelectedIds([])} />
        </div>
      )}

      {previewData && (
        <BulkPreviewModal
          drafts={previewData.drafts}
          stage={previewData.stage}
          job={previewData.job}
          onApprove={handleApproveAndSend}
          onClose={() => setPreviewData(null)}
          sending={sendingInvites}
        />
      )}

      <ResumeUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={handleUpload}
        accept=".pdf,.docx"
        isUploading={uploadProgress.isUploading}
        jobId={jobId}
        onBankAttached={() => { setIsModalOpen(false); fetchResumes(pagination.page, currentFilter); }}
      />

    </div>
  );
  };

export default ResumeManager;
